import React, { useState } from 'react';
import PageLayout from '../layouts/PageLayout';
import '../styles/pages/index.css';
import Input from '../components/Input';
import Button from '../components/Button';
import { useTheme } from '../hooks/useTheme';
import SubmitButtonLabel from '../components/SubmitButtonLabel';
import { useRouteIsLoading } from 'context/RouteIsLoadingContext';
import { useEffect } from 'react';
import { useSanityMetaTags } from '../hooks/useSanityMetaTags';
import { submitContactForm } from '../configs/sanity';

const Contact = () => {
  const { isDarkMode } = useTheme();
  const { setIsLoading: setRouteContextIsLoading } = useRouteIsLoading();
  useSanityMetaTags('contact');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: '',
  });
  const [submitStatus, setSubmitStatus] = useState('idle');
  const isLocked = submitStatus === 'loading' || submitStatus === 'success';
  const [validationErrors, setValidationErrors] = useState({
    name: false,
    email: false,
    company: false,
    phone: false,
  });

  useEffect(() => {
    if (submitStatus !== 'error') return;
    const timer = setTimeout(() => setSubmitStatus('idle'), 5000);
    return () => clearTimeout(timer);
  }, [submitStatus]);

  useEffect(() => {
    setRouteContextIsLoading(false);
  }, [setRouteContextIsLoading]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (submitStatus === 'error') {
      setSubmitStatus('idle');
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: false,
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    const trimmed = raw.trim();
    const withPlus = trimmed.startsWith('00')
      ? `+${trimmed.slice(2)}`
      : trimmed;
    const cleaned = withPlus.replace(/(?!^)[^\d]/g, '');
    if (cleaned.startsWith('+')) return cleaned;
    if (/^\d+$/.test(cleaned)) return `+${cleaned}`;
    return cleaned;
  };

  const isE164 = (value) => {
    return /^\+[1-9]\d{7,14}$/.test(value);
  };

  const validatePhone = (phone) => {
    if (!phone.trim()) return true;
    const normalized = normalizePhone(phone);
    return isE164(normalized);
  };

  const validateForm = () => {
    const errors = {
      name: !formData.name.trim(),
      email: !formData.email.trim() || !validateEmail(formData.email),
      company: !formData.company.trim(),
      phone: !validatePhone(formData.phone),
    };

    setValidationErrors(errors);
    return !Object.values(errors).some((error) => error);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitStatus('loading');

    submitContactForm(formData)
      .then(() => {
        setSubmitStatus('success');

        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            company: '',
            phone: '',
            message: '',
          });
          setValidationErrors({
            name: false,
            email: false,
            company: false,
            phone: false,
          });
          setSubmitStatus('idle');
        }, 5000);
      })
      .catch((error) => {
        console.error(error);
        setSubmitStatus('error');
      });
  };

  return (
    <PageLayout>
      <div className='page-wrapper contact-page-wrapper'>
        <div className='flex contact-page'>
          <div className='flex'>
            <p className='balanced-text uppercase full-width sec-nd-hp-p font-primary'>
              <span>Wie kann ich </span>
              <span className='font-secondary non-active'>sie Erreichen?</span>
            </p>
          </div>
          <form
            className={`flex contact-form ${!isDarkMode ? 'light-form' : ''}`}
            onSubmit={handleSubmit}
          >
            <Input
              disabled={isLocked}
              placeholder='Name*'
              name='name'
              value={formData.name}
              onChange={handleInputChange}
              className={`${validationErrors.name ? 'validation-error' : ''} ${
                isLocked ? 'loading' : ''
              }`}
            />
            <Input
              disabled={isLocked}
              placeholder='E-Mail*'
              name='email'
              value={formData.email}
              onChange={handleInputChange}
              className={`${validationErrors.email ? 'validation-error' : ''} ${
                isLocked ? 'loading' : ''
              }`}
            />
            <Input
              disabled={isLocked}
              placeholder='Firmenname*'
              name='company'
              value={formData.company}
              onChange={handleInputChange}
              className={`${
                validationErrors.company ? 'validation-error' : ''
              } ${isLocked ? 'loading' : ''}`}
            />
            <Input
              disabled={isLocked}
              type='tel'
              inputMode='tel'
              autoComplete='tel'
              placeholder='Phone (e.g., +49 15123456789)'
              name='phone'
              value={formData.phone}
              onChange={handleInputChange}
              onBlur={(e) => {
                const normalized = normalizePhone(e.target.value);
                setFormData((prev) => ({ ...prev, phone: normalized }));
              }}
              pattern='^\+[1-9]\d{7,14}$'
              maxLength='16'
              title='Enter a valid number in E.164 format, e.g., +4915123456789'
              className={`${validationErrors.phone ? 'validation-error' : ''} ${
                isLocked ? 'loading' : ''
              }`}
            />
            <textarea
              disabled={isLocked}
              placeholder='Hinterlassen Sie eine Nachricht'
              name='message'
              value={formData.message}
              onChange={handleInputChange}
              maxLength='2000'
              className={isLocked ? 'loading' : ''}
            />
            <Button
              className='flex justify-center'
              type='submit'
              disabled={isLocked}
            >
              <SubmitButtonLabel status={submitStatus} />
            </Button>
          </form>
        </div>
      </div>
    </PageLayout>
  );
};

export default Contact;
